import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { getTranslator, Translator } from '../../Translator';
import { Link, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { NotFound } from '../NotFound';
import moment from 'moment';
import { ElectionState } from '../../_generatedTypes.g';
import { DocumentTitle } from '../DocumentTitle';
import { useDispatch } from 'react-redux';
import { RenderErrors } from '../ui/ErrorComponent';
import { getElectionById } from '../../selectors';
import { ElectionLogo } from './ElectionItem';
import { FileInput } from '../ui/FileInput';
import { cancelIcon } from '../ui/icons';
import { api, ReadAs } from '../../api';
import { Warning } from '../ui/Warning';
import { cast } from "../../tools";
import { electionReplaced } from '../../features/election/electionsSlice';
import { QuestionMark } from '../ui/QuestionMark';
import { useAppSelector } from '../../hooks';

const DatetimeField = (props: {
    getValue: () => string;
    setValue: (val: string) => void;
    t: Translator;
}) =>
    <DatePicker
        dateFormat={props.t['MM/dd/yyyy HH:mm']}
        onChange={val => val && props.setValue(cast(val, Date).toISOString())}
        selected={moment(props.getValue(), moment.ISO_8601).toDate()}
        timeInputLabel={props.t['Time']}
        showTimeInput />

export enum EditStatus {
    Pristine,
    Unsaved,
    Saving,
    Saved,
    Invalid,
    Conflict,
    UnknownError,
}

interface EditStatusBannerProps { status: EditStatus, errors: string[], t: Translator }

const EditStatusBanner = (props: EditStatusBannerProps) => {
    const t = props.t;

    switch (props.status) {
        case EditStatus.Pristine:
        case EditStatus.Unsaved:
            return <></>;
        case EditStatus.Saved:
            return <div className="statusbanner--ok">{t['Your changes were successfully saved.']}</div>
        case EditStatus.Saving:
            return <div className="statusbanner--ok">{t['Saving...']}</div>
        case EditStatus.Conflict:
            return <div className="statusbanner--warning">
                {t['Your changes were not saved, because someone else edited the same election in the meantime. Copy your changes to some other window, reload the page and try again.']}
            </div>
        case EditStatus.Invalid:
            return <div className="statusbanner--warning">
                {t['Illegal change']}
                <RenderErrors errors={props.errors} />
            </div>
        case EditStatus.UnknownError:
            return <div className="statusbanner--warning">
                {t['An error occurred during saving.']}
                <RenderErrors errors={props.errors} />
            </div>
    }
}

type ElectionEditorParams = {
    electionId: string,
};

export const ElectionEditor = () => {
    const elections = useAppSelector(state => state.elections);
    const language = useAppSelector(state => state.language);
    const parties = useAppSelector(state => state.parties);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);

    const dispatch = useDispatch();
    const t = getTranslator(language);
    const hasCandidacy = useAppSelector(state => state.clientState.HasCandidacy);
    const justDutch = useAppSelector(state => state.clientState.JustDutch);

    const electionId = Number(useParams<ElectionEditorParams>().electionId);

    const initialValues = getElectionById(elections, electionId);
    const hasParties = parties.filter(p => p.ElectionId === electionId).length > 0;

    const [election, setElection] = React.useState<ElectionState>(initialValues);
    const [status, setStatus] = React.useState<EditStatus>(EditStatus.Pristine);
    const [errors, setErrors] = React.useState<string[]>([]);

    if (!election) {
        return <NotFound />
    }

    const onSelectLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = function () {
                update({ Logo: (reader.result as string).split(',')[1], HasLogo: true });
            }
            reader.readAsDataURL(file);
        } else {
            update({ Logo: undefined, HasLogo: false });
        }
    };

    const deleteLogo = async () => {

        if (election.Logo !== initialValues.Logo) {
            // image has been selected but not yet uploaded. Simply revert
            setElection(
                {
                    ...election,
                    Logo: initialValues.Logo,
                    HasLogo: initialValues.HasLogo,
                }
            );
            return;
        }
        update({ Logo: undefined, HasLogo: false });

        setStatus(EditStatus.Saving);
        return await api(token).Elections.RemoveLogo<void>(election.ElectionId, {})
            .ok(updatedElection => {
                dispatch(electionReplaced(updatedElection));

                setStatus(EditStatus.Saved);
            }, ReadAs.Json)
            .anyStatus(errors => { setStatus(EditStatus.Conflict); setErrors(errors) }, ReadAs.ValidationErrors)
            .fail(() => setStatus(EditStatus.UnknownError));
    }

    const saveElectionEdits = async () => {
        setStatus(EditStatus.Saving);
        window.scrollTo(0, 0);

        return await api(token).Elections.Update<void>(initialValues.ElectionId, election)
            .ok(updatedElection => {
                dispatch(electionReplaced(updatedElection));
                setStatus(EditStatus.Saved);
                setElection(updatedElection);
            }, ReadAs.Json)
            .status(409, () => setStatus(EditStatus.Conflict))
            .status(400, errors => { setStatus(EditStatus.Invalid); setErrors(errors) }, ReadAs.ValidationErrors)
            .anyStatus(message => { setStatus(EditStatus.UnknownError); setErrors([message]) }, ReadAs.Text)
            .fail(() => setStatus(EditStatus.UnknownError));
    };

    const update = (latestEdit: Partial<ElectionState>) => {
        const updatedElection = { ...election, ...latestEdit };

        if (hasCandidacy) {
            if (moment(updatedElection['CandidacyUntilMoment'], moment.ISO_8601).isBefore(moment(updatedElection['CandidacyFromMoment'], moment.ISO_8601))) {
                if ('CandidacyUntilMoment' in latestEdit) {
                    updatedElection['CandidacyUntilMoment'] = updatedElection['CandidacyFromMoment'];
                } else {
                    updatedElection['CandidacyFromMoment'] = updatedElection['CandidacyUntilMoment'];
                }
            }

            if (moment(updatedElection['VisibleFromMoment'], moment.ISO_8601).isBefore(moment(updatedElection['CandidacyUntilMoment'], moment.ISO_8601))) {
                if ('VisibleFromMoment' in latestEdit) {
                    updatedElection['VisibleFromMoment'] = updatedElection['CandidacyUntilMoment'];
                } else {
                    updatedElection['CandidacyUntilMoment'] = updatedElection['VisibleFromMoment'];
                }
            }
        }

        if (moment(updatedElection['VoteOpenMoment'], moment.ISO_8601).isBefore(moment(updatedElection['VisibleFromMoment'], moment.ISO_8601))) {
            if ('VoteOpenMoment' in latestEdit) {
                updatedElection['VoteOpenMoment'] = updatedElection['VisibleFromMoment'];
            } else {
                updatedElection['VisibleFromMoment'] = updatedElection['VoteOpenMoment'];
            }
        }

        if (moment(updatedElection['VisibleUntilMoment'], moment.ISO_8601).isBefore(moment(updatedElection['VoteCloseMoment'], moment.ISO_8601))) {
            if ('VisibleUntilMoment' in latestEdit) {
                updatedElection['VisibleUntilMoment'] = updatedElection['VoteCloseMoment'];
            } else {
                updatedElection['VoteCloseMoment'] = updatedElection['VisibleUntilMoment'];
            }
        }

        if (moment(updatedElection['VoteCloseMoment'], moment.ISO_8601).isBefore(moment(updatedElection['VoteOpenMoment'], moment.ISO_8601))) {
            if ('VoteCloseMoment' in latestEdit) {
                updatedElection['VoteCloseMoment'] = updatedElection['VoteOpenMoment'];
            } else {
                updatedElection['VoteOpenMoment'] = updatedElection['VoteCloseMoment'];
            }
        }

        if (moment(updatedElection['VisibleUntilMoment'], moment.ISO_8601).isBefore(moment(updatedElection['VisibleFromMoment'], moment.ISO_8601))) {
            if ('VisibleUntilMoment' in latestEdit) {
                updatedElection['VisibleUntilMoment'] = updatedElection['VisibleFromMoment'];
            } else {
                updatedElection['VisibleFromMoment'] = updatedElection['VisibleUntilMoment'];
            }
        }

        setStatus(EditStatus.Unsaved);
        setElection(updatedElection);
    }

    const textAreaRows = 10;

    return <div>
        <form className="editor" onSubmit={e => { saveElectionEdits(); e.preventDefault(); }}>
            <EditStatusBanner status={status} errors={errors} t={t} />
            <h1>{t.editElection({ NL: initialValues.Name.NL, EN: initialValues.Name.EN })}</h1>
            <DocumentTitle title={t.editElection({ NL: initialValues.Name.NL, EN: initialValues.Name.EN })} />
            <label>
                {t['Election Code']}
                <input name="Code" type="text" lang="nl-NL" value={election.Code} onChange={e => update({ Code: e.target.value })} />
            </label>
            <label>
                {t['Name (NL)']}
                <input name="Name_NL" type="text" lang="nl-NL" value={election.Name.NL} onChange={e => update({ Name: { NL: e.target.value, EN: election.Name.EN } })} />
            </label>
            {!justDutch && <label>
                {t['Name (EN)']}
                <input name="Name_EN" type="text" lang="en-GB" value={election.Name.EN} onChange={e => update({ Name: { EN: e.target.value, NL: election.Name.NL } })} />
            </label>}
            <label>
                {t['Description (NL, max. 4000 characters)']}
                <TextareaAutosize maxLength={4000} minRows={textAreaRows} name="Description_NL" lang="nl-NL" value={election.Description.NL} onChange={e => update({ Description: { NL: e.currentTarget.value, EN: election.Description.EN } })} />
            </label>
            {!justDutch && <label>
                {t['Description (EN, max. 4000 characters)']}
                <TextareaAutosize maxLength={4000} minRows={textAreaRows} name="Description_EN" lang="en-GB" value={election.Description.EN} onChange={e => update({ Description: { EN: e.currentTarget.value, NL: election.Description.NL } })} />
            </label>}
            <label>
                {t['Seat count']}
                <input type="number" name="SeatCount" value={election.SeatCount ? String(election.SeatCount) : ''} onChange={e => update({ SeatCount: e.target.value ? Number(e.target.value) : null })} />
            </label>
            {hasCandidacy && <>
                <label className="electioneditor__datetimefield">
                    {t['Candidacy open']}
                    <DatetimeField t={t} getValue={() => election.CandidacyFromMoment} setValue={val => update({ CandidacyFromMoment: val })} />
                </label>
                <label className="electioneditor__datetimefield">
                    {t['Candidacy closed']}
                    <DatetimeField t={t} getValue={() => election.CandidacyUntilMoment} setValue={val => update({ CandidacyUntilMoment: val })} />
                </label>
            </>
            }
            <label className="electioneditor__datetimefield">
                {t['Visible from date']}
                <DatetimeField t={t} getValue={() => election.VisibleFromMoment} setValue={val => update({ VisibleFromMoment: val })} />
            </label>
            <label className="electioneditor__datetimefield">
                {t['Visible until date']}
                <DatetimeField t={t} getValue={() => election.VisibleUntilMoment} setValue={val => update({ VisibleUntilMoment: val })} />
            </label>
            <label className="electioneditor__datetimefield">
                <Warning>{t['Note: if the election is open parties and/or candidates can not be edited']}</Warning>
                {t['Open date']}
                <DatetimeField t={t} getValue={() => election.VoteOpenMoment} setValue={val => update({ VoteOpenMoment: val })} />
            </label>
            <label className="electioneditor__datetimefield">
                {t['Closing date']}
                <DatetimeField t={t} getValue={() => election.VoteCloseMoment} setValue={val => update({ VoteCloseMoment: val })} />
            </label>
            <label>
                <input id="RandomOrderParties" type="checkbox"
                    checked={election.RandomOrderParties}
                    onChange={e => update({ RandomOrderParties: e.target.checked })} /> {t['Show parties or candidates in random order']}
            </label>
            <label>
                {hasParties && <QuestionMark>
                    {t['Note: party- or candidates-system can not be set because the election already has parties and/or candidates']}
                </QuestionMark>}
                <input
                    name="CandidateSystem"
                    id="CandidateSystemNo"
                    disabled={hasParties}
                    checked={!election.CandidateSystem}
                    onChange={() => update({ CandidateSystem: false })}
                    type="radio" /> {t['Party System']}
                <input
                    name="CandidateSystem"
                    id="CandidateSystemYes"
                    disabled={hasParties}
                    checked={election.CandidateSystem}
                    onChange={() => update({ CandidateSystem: true })}
                    type="radio" /> {t['Candidates System']}
            </label>


            <label>{t['Logo']}</label>
            <FileInput
                accept=".svg"
                name="logo_upload"
                onChange={onSelectLogo}
                caption={t['Choose image file']} />
            <div style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                <ElectionLogo lang={t.languageCode}>{election}</ElectionLogo>
                {(election.Logo != null || election.HasLogo) && <div title={t['Delete']} className="deleteLogoBtn" onClick={() => deleteLogo()}>{cancelIcon}</div>}
            </div>
            <div className="buttonrow">
                <button type="submit" disabled={status === EditStatus.Pristine} className="button--important">{t['Save']}</button>
                <button type="button" disabled={status === EditStatus.Pristine} onClick={() => { setStatus(EditStatus.Pristine); setElection(initialValues) }}>{t['Reset']}</button>
                <Link className="linkbutton" to="/">{t['Back']}</Link>
            </div>
        </form>
    </div>
}

